import React, { useState, useEffect, useMemo, useRef } from 'react';
import { RouteEntry, RoutesManifest } from '../../types/manifest';
import { siteConfig, categoryDockItems, ghihinhEcosystemLinks } from '../../config/siteConfig';
import { SmartLink } from './SmartLink';
import {
  IconAward,
  IconChevronRight,
  IconClose,
  IconExternalLink,
  IconGrid,
  IconHeadphones,
  IconHome,
  IconMail,
  IconMapPin,
  IconMenu,
  IconMessage,
  IconPhone,
  IconSearch,
  IconShieldCheck,
  IconTruck,
  IconVideo
} from '../icons/Icons';

export interface SiteLayoutProps {
  currentRoute: RouteEntry;
  manifest?: RoutesManifest;
  onNavigate: (path: string) => void;
  children: React.ReactNode;
}

export const SiteLayout: React.FC<SiteLayoutProps> = ({
  currentRoute,
  manifest,
  onNavigate,
  children
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileTab, setMobileTab] = useState<'menu' | 'categories'>('categories');
  const [stickyNavOpen, setStickyNavOpen] = useState(false);
  const [activeStickyItem, setActiveStickyItem] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);

  useEffect(() => {
    setMobileMenuOpen(false);
    setStickyNavOpen(false);
    setActiveStickyItem(null);
    setSearchFocused(false);
  }, [currentRoute.path]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target as Node)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const openStickyNav = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setStickyNavOpen(true);
  };

  const scheduleCloseStickyNav = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = window.setTimeout(() => {
      setStickyNavOpen(false);
      setActiveStickyItem(null);
    }, 180);
  };

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q || !manifest) return [];
    return manifest.routes
      .filter(
        (r) =>
          r.indexable &&
          (r.type === 'product' || r.type === 'post') &&
          (r.title.toLowerCase().includes(q) || r.path.toLowerCase().includes(q))
      )
      .slice(0, 7);
  }, [searchQuery, manifest]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchResults.length > 0) {
      onNavigate(searchResults[0].path);
      setSearchFocused(false);
    } else {
      onNavigate('/shop/');
      setSearchFocused(false);
    }
  };

  return (
    <div className="website-wrapper" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Backdrop Dimmer when WoodMart Sticky Nav is Expanded (.wd-close-side) */}
      <div
        className={`wd-close-side ${stickyNavOpen ? 'wd-close-side-opened' : ''}`}
        onClick={() => {
          setStickyNavOpen(false);
          setActiveStickyItem(null);
        }}
        aria-hidden="true"
      />

      {/* 1. WOODMART LEFT STICKY VERTICAL ICON RAIL (.wd-sticky-nav) */}
      <aside
        className={`wd-sticky-nav ${stickyNavOpen ? 'wd-opened' : ''}`}
        onMouseEnter={openStickyNav}
        onMouseLeave={scheduleCloseStickyNav}
        aria-label="Danh mục sản phẩm cố định"
      >
        <div className="wd-sticky-nav-title">
          <IconMenu size={18} style={{ flexShrink: 0 }} />
          <span>DANH MỤC SẢN PHẨM</span>
        </div>

        <ul className="wd-nav-sticky">
          {categoryDockItems.map((cat) => {
            const isActive = currentRoute.path === cat.path;
            const hasMega = Boolean(cat.megaColumns && cat.megaColumns.length > 0);
            const hasSub = Boolean(cat.subItems && cat.subItems.length > 0);
            const hasDropdown = hasMega || hasSub;
            const isHovered = activeStickyItem === cat.slug;

            return (
              <li
                key={cat.slug}
                className={`${hasMega ? 'menu-mega-dropdown' : 'menu-simple-dropdown'} ${
                  hasDropdown ? 'menu-item-has-children' : ''
                } ${isHovered ? 'wd-opened' : ''}`}
                onMouseEnter={() => setActiveStickyItem(cat.slug)}
              >
                <SmartLink
                  to={cat.path}
                  onNavigate={onNavigate}
                  className={`woodmart-nav-link ${isActive ? 'active' : ''}`}
                >
                  <img
                    src={cat.icon}
                    alt={`Biểu tượng danh mục ${cat.name}`}
                    width={20}
                    height={20}
                    loading="lazy"
                    decoding="async"
                    className="wd-nav-img"
                  />
                  <span className="nav-link-text">{cat.name}</span>
                  {hasDropdown && (
                    <IconChevronRight size={13} className="wd-sticky-nav-arrow" />
                  )}
                </SmartLink>

                {/* 3-Column WoodMart Mega Menu Dropdown (e.g. Máy Quét Mã Vạch - elementor-1665) */}
                {stickyNavOpen && hasMega && cat.megaColumns && (
                  <div className="wd-dropdown-menu wd-dropdown-mega">
                    <div className="wd-mega-grid">
                      {cat.megaColumns.map((colGroups, colIdx) => (
                        <div key={colIdx} className="wd-mega-col">
                          {colGroups.map((group) => (
                            <div key={group.heading} style={{ marginBottom: '12px' }} className="wd-mega-group">
                              <SmartLink
                                to={group.headingPath || cat.path}
                                onNavigate={onNavigate}
                                className="wd-mega-heading"
                              >
                                {group.heading}
                              </SmartLink>
                              <ul className="wd-mega-sublist">
                                {group.items.map((sub) => (
                                  <li key={sub.label}>
                                    <SmartLink
                                      to={sub.path}
                                      onNavigate={onNavigate}
                                      className="wd-mega-sublink"
                                    >
                                      <span>{sub.label}</span>
                                      {sub.badge && (
                                        <span className="wd-menu-badge-green">{sub.badge}</span>
                                      )}
                                    </SmartLink>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      ))}

                      {cat.megaBanner && (
                        <div className="wd-mega-banner-col">
                          <SmartLink
                            to={cat.megaBanner.path}
                            onNavigate={onNavigate}
                            className="wd-mega-banner-link"
                          >
                            <img
                              src={cat.megaBanner.image}
                              alt={cat.megaBanner.alt}
                              width={183}
                              height={300}
                              loading="lazy"
                              decoding="async"
                            />
                          </SmartLink>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Simple Vertical Flyout Submenu (e.g. Phụ Kiện Công Nghệ) */}
                {stickyNavOpen && !hasMega && hasSub && cat.subItems && (
                  <div className="wd-dropdown-menu wd-dropdown-simple">
                    <ul className="wd-simple-sublist">
                      {cat.subItems.map((sub) => (
                        <li key={sub.label}>
                          <SmartLink
                            to={sub.path}
                            onNavigate={onNavigate}
                            className="wd-simple-sublink"
                          >
                            <span>{sub.label}</span>
                          </SmartLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        {/* Bottom Sticky Nav Content (elementor-10033: Zalo & YouTube) */}
        <div className="wd-sticky-nav-content">
          <a
            href={siteConfig.zaloUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="wd-sticky-bottom-item"
          >
            <img
              src="/wp-content/uploads/2025/02/Icon-zalo.svg"
              alt="Zalo"
              width={24}
              height={24}
              loading="lazy"
              decoding="async"
            />
            <span>Zalo Tư Vấn 24/7</span>
          </a>
          <a
            href={siteConfig.youtubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="wd-sticky-bottom-item"
          >
            <span className="wd-yt-badge">
              <IconVideo size={14} />
            </span>
            <span>Youtube Channel</span>
          </a>
        </div>
      </aside>

      {/* 2. WOODMART 2-ROW HEADER BUILDER (.whb-header) */}
      <header className="whb-header">
        {/* Row 1: General Header (White 80px) */}
        <div className="whb-general-header">
          <div className="container whb-general-inner">
            <SmartLink
              to="/"
              onNavigate={onNavigate}
              className="whb-logo"
              aria-label={siteConfig.companyName}
            >
              <img
                src={siteConfig.logoUrl}
                alt={siteConfig.companyName}
                width={300}
                height={112}
                loading="eager"
                fetchPriority="high"
              />
            </SmartLink>

            {/* Center Pill Search Form (.wd-search-form) */}
            <div className="wd-search-wrap" ref={searchWrapRef}>
              <form
                className="wd-search-form"
                onSubmit={handleSearchSubmit}
                {...({
                  toolname: 'search_pmcstech_catalog',
                  tooldescription:
                    'Tìm kiếm sản phẩm phần mềm quay video đóng hàng ECAMPMCS, máy quét mã vạch Opticon, thiết bị RFID và quà tặng công nghệ LED tại pmcstech.net & pmcstech.com'
                } as React.FormHTMLAttributes<HTMLFormElement>)}
              >
                <input
                  type="search"
                  name="query"
                  title="Từ khóa tên sản phẩm, mã thiết bị Opticon, phần mềm ECAMPMCS hoặc bài viết kỹ thuật cần tìm"
                  className="wd-search-input"
                  placeholder="Search for products"
                  value={searchQuery}
                  onFocus={() => setSearchFocused(true)}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSearchFocused(true);
                  }}
                  aria-label="Search for products"
                />
                <button type="submit" className="wd-search-submit" aria-label="Search">
                  <IconSearch size={18} />
                </button>
              </form>

              {searchFocused && searchQuery.trim().length > 0 && (
                <div className="search-dropdown">
                  {searchResults.length > 0 ? (
                    searchResults.map((item) => {
                      const thumb = item.images[0]?.localPath || siteConfig.ecamIconUrl;
                      return (
                        <SmartLink
                          key={item.path}
                          to={item.path}
                          onNavigate={onNavigate}
                          className="search-item"
                        >
                          <img src={thumb} alt={item.title} width={44} height={44} loading="lazy" />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: '13.5px',
                                fontWeight: 600,
                                color: '#242424',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {item.title}
                            </div>
                            <div style={{ fontSize: '12.5px', color: '#1C61E7', fontWeight: 700 }}>
                              {item.type === 'product'
                                ? item.price || 'Liên hệ báo giá'
                                : 'Bài viết & Giải pháp'}
                            </div>
                          </div>
                          <IconChevronRight size={14} style={{ color: '#94A3B8' }} />
                        </SmartLink>
                      );
                    })
                  ) : (
                    <div style={{ padding: '12px', fontSize: '13.5px', color: '#595959', textAlign: 'center' }}>
                      Không tìm thấy kết quả phù hợp. Xem toàn bộ tại{' '}
                      <SmartLink to="/shop/" onNavigate={onNavigate} style={{ color: '#1C61E7', fontWeight: 700 }}>
                        Cửa Hàng
                      </SmartLink>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right WoodMart Info Boxes (.wd-info-box from extracted_header.html) */}
            <div className="whb-info-boxes">
              <a href={siteConfig.phoneHref} className="wd-header-info-box">
                <div className="wd-header-info-svg">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <path d="M4.81815 18.5599C5.37836 18.5599 5.8325 18.1058 5.8325 17.5456C5.8325 16.9854 5.37836 16.5313 4.81815 16.5313C4.25794 16.5313 3.8038 16.9854 3.8038 17.5456C3.8038 18.1058 4.25794 18.5599 4.81815 18.5599Z" fill="#333333" />
                    <path fillRule="evenodd" clipRule="evenodd" d="M11.7132 0.562359C12.463 -0.187453 13.6787 -0.187453 14.4285 0.562359L17.5537 3.68753C18.643 4.77688 18.643 6.54306 17.5537 7.63241L16.6571 8.52898L23.471 15.3429L24.3676 14.4463C25.4569 13.357 27.2231 13.357 28.3125 14.4463L31.4376 17.5715C32.1875 18.3213 32.1875 19.537 31.4376 20.2868C28.6711 23.0534 24.3264 23.28 21.3013 20.9666V25.1532C21.3013 26.6938 20.0524 27.9427 18.5118 27.9427H17.6006L17.1862 29.8074C16.8604 31.2736 14.9743 31.6977 14.0522 30.5121L12.0537 27.9427H2.78946C1.24888 27.9427 0 26.6938 0 25.1532V15.5169C0 13.9763 1.24888 12.7275 2.78946 12.7275H12.9658L11.7132 11.4748C8.69982 8.46144 8.69981 3.57576 11.7132 0.562359ZM14.4873 14.249L19.7798 19.5414V25.1532C19.7798 25.8535 19.2121 26.4211 18.5118 26.4211H16.9903C16.6338 26.4211 16.325 26.6688 16.2477 27.0169L15.7009 29.4773C15.6544 29.6868 15.3849 29.7474 15.2532 29.578L13.0263 26.7148C12.8821 26.5295 12.6605 26.4211 12.4258 26.4211H2.78946C2.08919 26.4211 1.52152 25.8535 1.52152 25.1532V15.5169C1.52152 14.8166 2.08919 14.249 2.78946 14.249H14.4873ZM13.3526 1.63824C13.197 1.48262 12.9447 1.48262 12.7891 1.63824C10.3699 4.05744 10.3699 7.97975 12.7891 10.399L21.601 19.2109C24.0202 21.6301 27.9426 21.6301 30.3618 19.2109C30.5174 19.0553 30.5174 18.803 30.3618 18.6474L27.2366 15.5222C26.7414 15.027 25.9386 15.027 25.4435 15.5222L24.009 16.9567C23.7119 17.2538 23.2302 17.2538 22.9331 16.9567L15.0433 9.06692C14.7462 8.76982 14.7462 8.28814 15.0433 7.99104L16.4778 6.55654C16.973 6.06138 16.973 5.25857 16.4778 4.76341L13.3526 1.63824Z" fill="#333333" />
                  </svg>
                </div>
                <div>
                  <div className="wd-header-info-title">Hotline</div>
                  <div className="wd-header-info-sub">{siteConfig.phoneIntl}</div>
                </div>
              </a>

              <SmartLink to="/chinh-sach-giao-nhan-hang/" onNavigate={onNavigate} className="wd-header-info-box">
                <div className="wd-header-info-svg">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <path d="M22.5876 2.23814C22.7504 1.8519 22.5692 1.40686 22.1829 1.24413C20.9104 0.707956 19.4943 0.193345 17.9911 0.0059111C17.5752 -0.0459477 17.196 0.24917 17.1441 0.665075C17.0923 1.08098 17.3874 1.46018 17.8033 1.51204C19.0967 1.67331 20.3593 2.12275 21.5936 2.64283C21.9798 2.80557 22.4249 2.62438 22.5876 2.23814Z" fill="#333333" />
                    <path fillRule="evenodd" clipRule="evenodd" d="M5.75493 16C5.75493 21.6495 10.3505 26.2451 16 26.2451C21.6494 26.2451 26.245 21.6495 26.245 16C26.245 10.3571 21.656 5.69176 16 5.69176C10.344 5.69176 5.75493 10.3571 5.75493 16ZM13.2516 7.65981C10.0149 8.75219 7.60987 11.7144 7.30536 15.2411H11.4582C11.5317 12.8434 11.9507 10.6479 12.6114 8.98107C12.8009 8.503 13.0143 8.05814 13.2516 7.65981ZM12.9767 15.2411C13.0499 12.9997 13.4434 11.001 14.0224 9.54032C14.345 8.72625 14.7104 8.11707 15.0776 7.72406C15.4438 7.33203 15.756 7.20955 16 7.20955C16.2439 7.20955 16.5562 7.33203 16.9224 7.72406C17.2895 8.11707 17.6549 8.72625 17.9775 9.54032C18.5565 11.001 18.95 12.9997 19.0232 15.2411H12.9767ZM11.4582 16.7589H7.30543C7.60979 20.2738 10.0078 23.2002 13.242 24.2806C13.0088 23.8908 12.7989 23.4563 12.6123 22.9896C11.9508 21.336 11.5317 19.1541 11.4582 16.7589ZM18.7579 24.2806C21.9921 23.2002 24.3901 20.2738 24.6945 16.7589H20.5417C20.4682 19.1541 20.0491 21.336 19.3877 22.9896C19.201 23.4563 18.9911 23.8908 18.7579 24.2806ZM19.0232 16.7589C18.95 18.9973 18.5565 20.9807 17.9784 22.4259C17.6563 23.2313 17.2916 23.8322 16.9256 24.2193C16.561 24.6048 16.248 24.7273 16 24.7273C15.752 24.7273 15.4389 24.6048 15.0744 24.2193C14.7083 23.8322 14.3437 23.2313 14.0215 22.4259C13.4434 20.9807 13.05 18.9973 12.9768 16.7589H19.0232ZM20.5417 15.2411H24.6946C24.3901 11.7144 21.985 8.75219 18.7483 7.65981C18.9857 8.05815 19.1991 8.503 19.3885 8.98107C20.0492 10.6479 20.4683 12.8434 20.5417 15.2411Z" fill="#333333" />
                    <path d="M28.6977 6.30483C28.9512 6.63855 28.8862 7.11464 28.5525 7.36821C28.2188 7.62177 27.7427 7.5568 27.4891 7.22307C26.694 6.17652 25.7603 5.24285 24.7137 4.44769C24.38 4.19413 24.315 3.71804 24.5685 3.38431C24.8221 3.05058 25.2982 2.98559 25.6319 3.23915C26.788 4.11749 27.8193 5.14881 28.6977 6.30483Z" fill="#333333" />
                    <path d="M31.3348 14.8558C31.7507 14.804 32.0459 14.4248 31.994 14.0089C31.8055 12.4962 31.221 11.008 30.6926 9.75378C30.5299 9.36754 30.0849 9.18635 29.6986 9.34908C29.3124 9.51181 29.1312 9.95684 29.2939 10.3431C29.8218 11.5959 30.3279 12.9127 30.4879 14.1966C30.5397 14.6125 30.9189 14.9077 31.3348 14.8558Z" fill="#333333" />
                    <path d="M16 32C7.19837 32 0 24.8016 0 16C0 11.7905 1.67904 7.91831 4.35243 5.0593H3.53315C3.11403 5.0593 2.77426 4.71953 2.77426 4.3004C2.77426 3.88128 3.11403 3.54151 3.53315 3.54151H6.26086C6.67998 3.54151 7.01975 3.88128 7.01975 4.3004V6.93888C7.01975 7.358 6.67998 7.69777 6.26086 7.69777C5.84173 7.69777 5.50197 7.358 5.50197 6.93888V6.05225C3.05332 8.64925 1.51779 12.1757 1.51779 16C1.51779 23.9634 8.03662 30.4822 16 30.4822C23.3591 30.4822 29.602 24.9085 30.488 17.8034C30.5398 17.3875 30.919 17.0923 31.3349 17.1442C31.7508 17.1961 32.0459 17.5753 31.9941 17.9912C31.0128 25.8601 24.1208 32 16 32Z" fill="#333333" />
                    <path d="M9.41225 2.23813C9.57498 2.62437 10.02 2.80556 10.4063 2.64283C11.6407 2.12274 12.9031 1.67331 14.1966 1.51204C14.6125 1.46018 14.9076 1.08098 14.8557 0.665075C14.8039 0.24917 14.4247 -0.0459477 14.0088 0.0059111C12.5056 0.193345 11.0895 0.70796 9.81695 1.24412C9.43071 1.40685 9.24951 1.85189 9.41225 2.23813Z" fill="#333333" />
                  </svg>
                </div>
                <div>
                  <div className="wd-header-info-title">VietNam</div>
                  <div className="wd-header-info-sub">Free Shipping</div>
                </div>
              </SmartLink>
            </div>

            <button
              type="button"
              className="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open mobile menu"
            >
              <IconMenu size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* Row 2: Sticky Ice-Blue Header Bottom (.whb-header-bottom) */}
      <div className="whb-header-bottom">
        <div className="container whb-bottom-inner">
          {/* Authentic WoodMart wd-design-8 "Danh Mục Sản Phẩm" Button */}
          <div
            className="wd-header-cats"
            onMouseEnter={openStickyNav}
            onMouseLeave={scheduleCloseStickyNav}
          >
            <button
              type="button"
              className="wd-header-cats-btn"
              onClick={() => {
                if (typeof window !== 'undefined' && window.innerWidth < 1025) {
                  setMobileTab('categories');
                  setMobileMenuOpen(true);
                } else {
                  setStickyNavOpen((prev) => !prev);
                }
              }}
              aria-expanded={stickyNavOpen}
            >
              <span className="wd-tools-icon-circle">
                <IconMenu size={16} />
              </span>
              <span className="wd-tools-text">Danh Mục Sản Phẩm</span>
            </button>

            <SmartLink
              to="/shop/"
              onNavigate={onNavigate}
              className="wd-mobile-shop-quick-btn"
            >
              <IconGrid size={14} />
              <span>Cửa Hàng</span>
            </SmartLink>
          </div>

          <nav className="wd-nav-scroll" aria-label="Main navigation">
            <ul className="wd-main-nav">
              {siteConfig.primaryLinks.map((link) => {
                const isActive =
                  link.path === '/'
                    ? currentRoute.path === '/'
                    : currentRoute.path.startsWith(link.path);
                return (
                  <li key={link.path}>
                    <SmartLink
                      to={link.path}
                      onNavigate={onNavigate}
                      className={isActive ? 'active' : undefined}
                    >
                      <span className="wd-nav-label-desktop">{link.label}</span>
                      <span className="wd-nav-label-mobile">{link.mobileLabel || link.label}</span>
                    </SmartLink>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="whb-bottom-right" />
        </div>
      </div>

      {/* Mobile Slide-Out Drawer (with Menu / Categories Tabs matching WoodMart) */}
      {mobileMenuOpen && (
        <div className="mobile-drawer-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div className="mobile-drawer" onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingBottom: '12px',
                borderBottom: '1px solid #EAEAEA'
              }}
            >
              <img src={siteConfig.logoUrl} alt="PMCS TECH" width={140} height={42} style={{ objectFit: 'contain' }} />
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Đóng menu"
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '8px',
                  border: '1px solid #E2E8F0',
                  background: '#F8FAFC',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <IconClose size={18} />
              </button>
            </div>

            {/* WoodMart Mobile Drawer Tabs: Categories | Menu */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: '2px solid #EAEAEA' }}>
              <button
                type="button"
                onClick={() => setMobileTab('categories')}
                style={{
                  padding: '10px',
                  minHeight: '44px',
                  fontWeight: 700,
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  border: 'none',
                  background: mobileTab === 'categories' ? '#E6EFFD' : 'transparent',
                  color: mobileTab === 'categories' ? '#0F3E99' : '#555555',
                  cursor: 'pointer'
                }}
              >
                Danh Mục
              </button>
              <button
                type="button"
                onClick={() => setMobileTab('menu')}
                style={{
                  padding: '10px',
                  minHeight: '44px',
                  fontWeight: 700,
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  border: 'none',
                  background: mobileTab === 'menu' ? '#E6EFFD' : 'transparent',
                  color: mobileTab === 'menu' ? '#0F3E99' : '#555555',
                  cursor: 'pointer'
                }}
              >
                Menu
              </button>
            </div>

            {mobileTab === 'categories' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {categoryDockItems.map((cat) => (
                  <div key={cat.slug}>
                    <SmartLink
                      to={cat.path}
                      onNavigate={onNavigate}
                      className="mobile-drawer-link"
                      style={{ padding: '10px', fontSize: '13.5px' }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                        <img src={cat.icon} alt={`Biểu tượng ${cat.name}`} width={20} height={20} />
                        <span>{cat.name}</span>
                      </span>
                      <IconChevronRight size={14} />
                    </SmartLink>
                    {cat.megaColumns && (
                      <div style={{ paddingLeft: '36px', display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '4px' }}>
                        {cat.megaColumns.map((colGroups, colIdx) => (
                          <div key={colIdx} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {(colGroups || []).map((group) => (
                              <div key={group.heading} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <SmartLink
                                  to={group.headingPath || cat.path}
                                  onNavigate={onNavigate}
                                  style={{ padding: '7px 10px', fontSize: '12px', fontWeight: 700, color: '#0F3E99', textTransform: 'uppercase' }}
                                >
                                  {group.heading}
                                </SmartLink>
                                {(group.items || []).map((item) => (
                                  <SmartLink
                                    key={item.label}
                                    to={item.path}
                                    onNavigate={onNavigate}
                                    style={{ padding: '6px 10px 6px 20px', fontSize: '12.5px', color: '#475569' }}
                                  >
                                    {item.label}
                                  </SmartLink>
                                ))}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                    {cat.subItems && (
                      <div style={{ paddingLeft: '36px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {cat.subItems.map((sub) => (
                          <SmartLink
                            key={sub.label}
                            to={sub.path}
                            onNavigate={onNavigate}
                            style={{ padding: '9px 10px', fontSize: '12.5px', color: '#475569' }}
                          >
                            {sub.label}
                          </SmartLink>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {siteConfig.primaryLinks.map((item) => {
                  const isActive =
                    item.path === '/'
                      ? currentRoute.path === '/'
                      : currentRoute.path.startsWith(item.path);
                  return (
                    <SmartLink
                      key={item.path}
                      to={item.path}
                      onNavigate={onNavigate}
                      className={`mobile-drawer-link ${isActive ? 'active' : ''}`}
                    >
                      <span>{item.label}</span>
                      <IconChevronRight size={15} />
                    </SmartLink>
                  );
                })}
              </div>
            )}

            <div
              style={{
                marginTop: 'auto',
                paddingTop: '14px',
                borderTop: '1px solid #EAEAEA',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}
            >
              <a href={siteConfig.phoneHref} className="btn-orange" style={{ width: '100%' }}>
                <IconPhone size={16} />
                <span>Hotline: {siteConfig.phoneDisplay}</span>
              </a>
              <a
                href={siteConfig.zaloUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                style={{ width: '100%' }}
              >
                <IconMessage size={16} />
                <span>Chat Zalo OA Tư Vấn</span>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Main Page Content */}
      <main style={{ flex: 1 }}>{children}</main>

      {/* 3. WOODMART FOOTER (.wd-footer matching elementor-128) */}
      <footer className="wd-footer">
        {/* Top 4 Service Info Boxes on #E4E4E4 (elementor-element-cc1be2a) */}
        <div className="wd-footer-perks">
          <div className="container wd-footer-perks-grid">
            <div className="wd-footer-perk-box">
              <div className="wd-footer-perk-header">
                <div className="wd-footer-perk-title">Giao Hàng Siêu Tốc</div>
                <span className="wd-footer-perk-icon">
                  <IconTruck size={22} />
                </span>
              </div>
              <p className="wd-footer-perk-desc">
                Miễn phí vận chuyển Toàn Quốc. Giao siêu tốc 2-4H trong TPHCM
              </p>
            </div>

            <div className="wd-footer-perk-box">
              <div className="wd-footer-perk-header">
                <div className="wd-footer-perk-title">7 Ngày Đổi Trả</div>
                <span className="wd-footer-perk-icon">
                  <IconAward size={22} />
                </span>
              </div>
              <p className="wd-footer-perk-desc">
                Yên tâm mua sắm với chính sách đổi trả trong vòng 7 ngày
              </p>
            </div>

            <div className="wd-footer-perk-box">
              <div className="wd-footer-perk-header">
                <div className="wd-footer-perk-title">100% Chính Hãng</div>
                <span className="wd-footer-perk-icon">
                  <IconShieldCheck size={22} />
                </span>
              </div>
              <p className="wd-footer-perk-desc">
                Nhập khẩu chính ngạch. Cam kết chất lượng sản phẩm chính hãng 100%
              </p>
            </div>

            <div className="wd-footer-perk-box">
              <div className="wd-footer-perk-header">
                <div className="wd-footer-perk-title">Support 24/7</div>
                <span className="wd-footer-perk-icon">
                  <IconHeadphones size={22} />
                </span>
              </div>
              <p className="wd-footer-perk-desc">
                Hỗ trợ khách hàng nhanh chóng
              </p>
            </div>
          </div>
        </div>

        {/* Main 4 Footer Columns (elementor-128: 20% / 25% / 20% / 35%) */}
        <div className="wd-footer-main">
          <div className="container">
            <div className="footer-grid">
              {/* Col 1: Logo + Intro + Social */}
              <div>
                <SmartLink to="/" onNavigate={onNavigate} style={{ display: 'inline-block', marginBottom: '14px' }}>
                  <img
                    src={siteConfig.footerLogoUrl}
                    alt={siteConfig.companyName}
                    width={170}
                    height={49}
                    loading="lazy"
                    decoding="async"
                    style={{ width: '170px', height: '49px', objectFit: 'contain' }}
                  />
                </SmartLink>
                <p style={{ fontSize: '14px', lineHeight: 1.65, color: '#121212', marginBottom: '18px', maxWidth: '290px' }}>
                  Pmcstech.net – Nền tảng mua sắm thiết bị mã vạch, phần mềm ghi hình đóng hàng ECAMPMCS và phụ kiện công nghệ cao cấp.
                </p>
                <div className="footer-heading" style={{ marginBottom: '10px' }}>HỆ SINH THÁI &amp; SOCIAL</div>
                <ul className="footer-links">
                  <li>
                    <a href="https://ghihinh.com/" target="_blank" rel="noopener">
                      Phần mềm quay video đóng hàng — Ghihinh.com
                    </a>
                  </li>
                  <li>
                    <a href={siteConfig.sisterDomainUrl} target="_blank" rel="noopener">
                      Thiết bị UHF RFID — Pmcstech.com
                    </a>
                  </li>
                  <li>
                    <a href={siteConfig.youtubeUrl} target="_blank" rel="noopener noreferrer">
                      Youtube PMCS TECH
                    </a>
                  </li>
                  <li>
                    <a href={siteConfig.zaloUrl} target="_blank" rel="noopener noreferrer">
                      Zalo OA Tư Vấn
                    </a>
                  </li>
                </ul>
              </div>

              {/* Col 2: THÔNG TIN */}
              <div>
                <div className="footer-heading">THÔNG TIN & DỮ LIỆU</div>
                <ul className="footer-links">
                  <li><SmartLink to="/gioi-thieu/" onNavigate={onNavigate}>Giới Thiệu</SmartLink></li>
                  <li><SmartLink to="/bai-viet-tin-tuc/" onNavigate={onNavigate}>Tin Công Ty</SmartLink></li>
                  <li><SmartLink to="/khuyen-mai/" onNavigate={onNavigate}>Tin Khuyến Mãi</SmartLink></li>
                  <li><SmartLink to="/sitemap/" onNavigate={onNavigate}>Sơ Đồ Trang Web (Sitemap)</SmartLink></li>
                  <li><SmartLink to="/ai-data/" onNavigate={onNavigate}>Dữ Liệu Cho AI Học (LLMs / RAG)</SmartLink></li>
                  <li><SmartLink to="/lien-he/" onNavigate={onNavigate}>Liên Hệ & Tuyển Dụng</SmartLink></li>
                </ul>
              </div>

              {/* Col 3: CHÍNH SÁCH CHUNG */}
              <div>
                <div className="footer-heading">CHÍNH SÁCH CHUNG</div>
                <ul className="footer-links">
                  <li><SmartLink to="/dieu-khoan-su-dung/" onNavigate={onNavigate}>Điều Khoản Sử Dụng</SmartLink></li>
                  <li><SmartLink to="/chinh-sach-bao-mat-thong-tin/" onNavigate={onNavigate}>Chính Sách Bảo Mật Thông Tin</SmartLink></li>
                  <li><SmartLink to="/chinh-sach-thanh-toan/" onNavigate={onNavigate}>Chính Sách Thanh Toán</SmartLink></li>
                  <li><SmartLink to="/chinh-sach-giao-nhan-hang/" onNavigate={onNavigate}>Chính Sách Giao Nhận</SmartLink></li>
                  <li><SmartLink to="/chinh-sach-doi-tra-san-pham/" onNavigate={onNavigate}>Chính Sách Đổi Trả Sản Phẩm</SmartLink></li>
                  <li><SmartLink to="/chinh-sach-bao-hanh/" onNavigate={onNavigate}>Chính Sách Bảo Hành</SmartLink></li>
                </ul>
              </div>

              {/* Col 4: LIÊN HỆ */}
              <div>
                <div className="footer-heading">LIÊN HỆ</div>
                <ul className="wd-footer-contact-list">
                  <li>
                    <IconHome size={14} className="wd-contact-icon" />
                    <span>{siteConfig.legalName}</span>
                  </li>
                  <li>
                    <IconExternalLink size={14} className="wd-contact-icon" />
                    <span>{siteConfig.englishName}</span>
                  </li>
                  <li>
                    <IconShieldCheck size={14} className="wd-contact-icon" />
                    <span>Mã số thuế: {siteConfig.taxId}</span>
                  </li>
                  <li>
                    <IconMapPin size={14} className="wd-contact-icon" />
                    <span>Địa chỉ: {siteConfig.address.full}, Việt Nam</span>
                  </li>
                  <li>
                    <IconAward size={14} className="wd-contact-icon" />
                    <span>Ngày hoạt động: {siteConfig.foundingDate}</span>
                  </li>
                  <li>
                    <IconPhone size={14} className="wd-contact-icon" />
                    <span>
                      Hotline/ Zalo : <a href={siteConfig.phoneHref} style={{ color: '#1C61E7', fontWeight: 600 }}>{siteConfig.phoneRaw}</a>
                    </span>
                  </li>
                  <li>
                    <IconMail size={14} className="wd-contact-icon" />
                    <span>
                      Email: <a href={`mailto:${siteConfig.email}`} style={{ color: '#1C61E7', fontWeight: 600 }}>{siteConfig.email}</a>
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Cross-Domain Ecosystem Authority Bar (Boosting ghihinh.com & Sister Domains) */}
            <div
              style={{
                marginTop: '24px',
                paddingTop: '18px',
                borderTop: '1px solid #E2E8F0',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}
            >
              <div style={{ fontSize: '12px', fontWeight: 800, color: '#0F172A', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Hệ Sinh Thái Giải Pháp Ghi Hình Đóng Hàng, Camera Sàn TMĐT &amp; Công Nghệ Liên Kết:
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px 16px',
                  fontSize: '12.5px',
                  lineHeight: 1.6
                }}
              >
                {ghihinhEcosystemLinks.map((ext) => (
                  <a
                    key={ext.url}
                    href={ext.url}
                    target="_blank"
                    rel="noopener"
                    title={ext.desc}
                    style={{
                      color: '#1E293B',
                      textDecoration: 'none',
                      fontWeight: 600,
                      borderBottom: '1px dashed #94A3B8',
                      paddingBottom: '1px'
                    }}
                  >
                    {ext.anchor}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="copyrights-wrapper">
          <div className="container footer-bottom">
            <div>
              © 2025 Bản quyền thuộc về {siteConfig.legalName} | pmcstech.net
            </div>
            <div>
              <img
                src="/wp-content/themes/woodmart/images/payments.png"
                alt="payments"
                width={255}
                height={22}
                loading="lazy"
                decoding="async"
                style={{ width: '255px', height: '22px', objectFit: 'contain' }}
              />
            </div>
          </div>
        </div>
      </footer>

      {/* Desktop Floating Conversion Dock */}
      <aside className="floating-dock" aria-label="Liên hệ nhanh">
        <a
          href={siteConfig.zaloUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="dock-btn dock-btn-zalo"
        >
          <IconMessage size={16} />
          <span>Chat Zalo OA</span>
        </a>
        <a href={siteConfig.phoneHref} className="dock-btn dock-btn-call">
          <IconPhone size={16} />
          <span>{siteConfig.phoneDisplay}</span>
        </a>
      </aside>

      {/* Mobile Bottom Sticky Action Bar */}
      <nav className="mobile-bottom-bar" aria-label="Thanh công cụ di động">
        <div className="mobile-bottom-grid">
          <SmartLink to="/" onNavigate={onNavigate} className="mobile-tab-btn">
            <IconHome size={18} />
            <span>Trang chủ</span>
          </SmartLink>
          <SmartLink to="/shop/" onNavigate={onNavigate} className="mobile-tab-btn">
            <IconGrid size={18} />
            <span>Cửa hàng</span>
          </SmartLink>
          <SmartLink
            to="/product/phan-mem-quay-video-dong-hang-ecampmcs/"
            onNavigate={onNavigate}
            className="mobile-tab-btn"
          >
            <IconVideo size={18} />
            <span>ECAMPMCS</span>
          </SmartLink>
          <a href={siteConfig.phoneHref} className="mobile-tab-btn cta-call">
            <IconPhone size={18} />
            <span>Gọi Ngay</span>
          </a>
          <a
            href={siteConfig.zaloUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mobile-tab-btn cta-zalo"
          >
            <IconMessage size={18} />
            <span>Chat Zalo</span>
          </a>
        </div>
      </nav>
    </div>
  );
};
